// PromoWeekendScreen.jsx — Fase 2 UI Pelanggan
// Spec: Sabtu & Minggu x Pagi 08-11 & Malam 19-21 @10 slot (40 slot/minggu).
// Tiap menu 10rb, 1 akun max 2 slot/sesi, bayar depan pax*10000+500 via Duitku QRIS, hangus jika no-show.
import React, { useState, useEffect } from 'react';
import { app, db, appId } from '../firebase';
import {
    collection, doc, getDoc, query, where, orderBy, limit,
    onSnapshot
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { QRCodeSVG } from 'qrcode.react';

const functions = getFunctions(app);
const publicDataPath = `artifacts/${appId}/public/data`;
const TICKET_PRICE = 10000;
const ADMIN_FEE = 500;

const getTodayStr = () => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const formatSlotLabel = (slot) => {
    const dayName = slot.day === 'sabtu' ? 'Sabtu' : slot.day === 'minggu' ? 'Minggu' : (slot.day || '');
    const sesiName = slot.sesi === 'pagi' ? 'Pagi 08.00–11.00' : 'Malam 19.00–21.00';
    return `${dayName} ${slot.date} • ${sesiName}`;
};

const getStatusBadge = (slot) => {
    const sisa = Number(slot.kuotaTotal || 10) - Number(slot.terisi || 0);
    if (slot.status === 'full' || sisa <= 0) {
        return <span className="text-xs font-bold px-2 py-1 bg-red-100 text-red-700 rounded-full">FULL</span>;
    }
    if (slot.status === 'closed') {
        return <span className="text-xs font-bold px-2 py-1 bg-gray-200 text-gray-600 rounded-full">DITUTUP</span>;
    }
    if (slot.status === 'expired') {
        return <span className="text-xs font-bold px-2 py-1 bg-gray-200 text-gray-500 rounded-full">BERAKHIR</span>;
    }
    return <span className="text-xs font-bold px-2 py-1 bg-green-100 text-green-800 rounded-full">Sisa {sisa}</span>;
};

const PromoWeekendScreen = ({ user, showNotification, onBack }) => {
    const [slots, setSlots] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedSlotId, setSelectedSlotId] = useState(null);
    const [pax, setPax] = useState(1);
    const [isBooking, setIsBooking] = useState(false);
    const [myBookings, setMyBookings] = useState([]);
    const [promoReturn, setPromoReturn] = useState(null);

    // Realtime slot weekend ini (transparan kuota)
    useEffect(() => {
        const todayStr = getTodayStr();
        const q = query(
            collection(db, `${publicDataPath}/promo_slots`),
            where('date', '>=', todayStr),
            orderBy('date', 'asc'),
            limit(8)
        );
        const unsub = onSnapshot(q, (snap) => {
            const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            // Tampilkan max 4 slot terdekat yang open/full (sembunyikan expired/closed dari daftar utama)
            const visible = list.filter((s) => ['open', 'full'].includes(s.status)).slice(0, 4);
            setSlots(visible.length > 0 ? visible : list.slice(0, 4));
            setLoading(false);
        }, (err) => {
            console.error('Promo slots listener error:', err);
            setLoading(false);
        });
        return () => unsub();
    }, []);

    // Booking milik user (untuk QR + status + tombol bayar ulang)
    useEffect(() => {
        if (!user?.uid) return;
        const q = query(
            collection(db, `${publicDataPath}/transactions`),
            where('customerId', '==', user.uid),
            where('type', '==', 'promo_booking'),
            limit(20)
        );
        const unsub = onSnapshot(q, (snap) => {
            const list = snap.docs
                .map((d) => ({ id: d.id, ...d.data() }))
                .sort((a, b) => {
                    const tA = a.date?.seconds || a.createdAt?.seconds || 0;
                    const tB = b.date?.seconds || b.createdAt?.seconds || 0;
                    return tB - tA;
                });
            setMyBookings(list);
        }, (err) => {
            console.error('My promo bookings listener error:', err);
        });
        return () => unsub();
    }, [user?.uid]);

    // Handle return dari Duitku (?promo_success=1&booking_id=...)
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('promo_success') === '1' && params.get('booking_id')) {
            const bookingId = params.get('booking_id');
            getDoc(doc(db, `${publicDataPath}/transactions`, bookingId))
                .then((snap) => {
                    if (snap.exists()) {
                        setPromoReturn({ id: snap.id, ...snap.data() });
                        showNotification('Pembayaran promo diterima! Tunjukkan QR saat datang.', 'success');
                    }
                })
                .catch((e) => console.error('Fetch promo return error:', e))
                .finally(() => {
                    window.history.replaceState({}, '', window.location.pathname);
                });
        }
    }, [showNotification]);

    const selectedSlot = slots.find((s) => s.id === selectedSlotId) || null;
    const totalBayar = selectedSlot ? (pax * TICKET_PRICE + ADMIN_FEE) : 0;

    const handleBooking = async () => {
        if (!selectedSlot) {
            showNotification('Pilih sesi dulu (Sabtu/Minggu Pagi/Malam).', 'error');
            return;
        }
        if (user?.isAnonymous) {
            window.dispatchEvent(new CustomEvent('open-login-prompt', { detail: { message: 'Login dulu untuk booking promo 10rb agar slot aman.' } }));
            return;
        }
        const sisa = Number(selectedSlot.kuotaTotal || 10) - Number(selectedSlot.terisi || 0);
        if (sisa < pax) {
            showNotification(`Sisa kuota ${sisa}, tidak cukup untuk ${pax} orang.`, 'error');
            return;
        }
        setIsBooking(true);
        try {
            const createPromoInvoice = httpsCallable(functions, 'createPromoInvoice');
            const result = await createPromoInvoice({
                slotId: selectedSlot.id,
                pax: pax,
                customerName: user.name || user.email || 'Pelanggan',
            });
            const data = result.data;
            showNotification('Slot terkunci! Mengalihkan ke pembayaran...', 'info');
            setTimeout(() => {
                if (data.paymentUrl) {
                    window.location.href = data.paymentUrl;
                }
            }, 1200);
        } catch (e) {
            console.error('Booking promo error:', e);
            const msg = e.message || 'Gagal booking. Coba lagi.';
            showNotification(msg, 'error');
        } finally {
            setIsBooking(false);
        }
    };

    const handleCheckStatus = async (booking) => {
        if (!booking.merchantOrderId) {
            showNotification('Merchant order tidak ditemukan.', 'error');
            return;
        }
        try {
            const checkDuitkuStatus = httpsCallable(functions, 'checkDuitkuStatus');
            const res = await checkDuitkuStatus({ merchantOrderId: booking.merchantOrderId });
            showNotification(`Status: ${res.data.status}`, res.data.status === 'success' ? 'success' : 'info');
        } catch (e) {
            showNotification(e.message || 'Gagal cek status.', 'error');
        }
    };

    if (loading) {
        return <div className="p-6 text-center text-gray-500">Memuat slot promo weekend...</div>;
    }

    return (
        <div className="p-4 md:p-6">
            <button onClick={onBack} className="text-sm text-gray-500 hover:text-green-700 mb-3">← Kembali</button>
            <h1 className="text-2xl font-bold text-green-900">Promo Weekend 10rb 🎉</h1>
            <p className="text-sm text-gray-600 mt-1">Sabtu & Minggu • Pagi 08–11 • Malam 19–21 • @10 slot per sesi</p>

            <div className="mt-3 bg-yellow-50 border border-yellow-200 rounded-xl p-3 text-sm text-yellow-900">
                <p className="font-bold">Aturan main:</p>
                <ul className="list-disc ml-5 mt-1 space-y-0.5">
                    <li>Tiap menu jadi Rp 10.000 saat sesi.</li>
                    <li>1 akun max 2 orang per sesi.</li>
                    <li>Bayar depan via QRIS: 1 orang Rp 10.500, 2 orang Rp 20.500.</li>
                    <li>1 tiket = 1 menu pertama. Nambah menu bayar 10rb/item di kasir.</li>
                    <li>Tidak datang = hangus.</li>
                </ul>
            </div>

            {promoReturn && (
                <div className="mt-4 bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                    <p className="font-bold text-green-900">Booking lunas! ✅</p>
                    <p className="text-sm text-gray-600">{promoReturn.promoSlotId} • {promoReturn.pax} orang • {promoReturn.kodeBooking}</p>
                    <div className="flex justify-center mt-3 bg-white p-3 rounded-xl inline-block">
                        <QRCodeSVG value={promoReturn.kodeBooking || promoReturn.id} size={150} level="M" includeMargin={false} />
                    </div>
                    <p className="text-xs text-gray-500 mt-2">Tunjukkan QR ini ke kasir saat sesi.</p>
                </div>
            )}

            <h2 className="mt-5 font-bold text-gray-800">Pilih Sesi (kuota live)</h2>
            {slots.length === 0 ? (
                <p className="text-sm text-gray-500 mt-2">Belum ada sesi dibuka. Tunggu admin generate weekend ini.</p>
            ) : (
                <div className="mt-2 space-y-3">
                    {slots.map((slot) => {
                        const terisi = Number(slot.terisi || 0);
                        const total = Number(slot.kuotaTotal || 10);
                        const sisa = total - terisi;
                        const pct = Math.min(100, Math.round((terisi / total) * 100));
                        const isSelected = selectedSlotId === slot.id;
                        const isFull = slot.status === 'full' || sisa <= 0;
                        return (
                            <button
                                key={slot.id}
                                disabled={slot.status === 'expired' || slot.status === 'closed'}
                                onClick={() => { setSelectedSlotId(slot.id); setPax(1); }}
                                className={`w-full text-left bg-white border rounded-xl p-4 shadow-sm ${isSelected ? 'border-green-600 ring-2 ring-green-200' : 'border-gray-100'} ${isFull ? 'opacity-70' : ''}`}
                            >
                                <div className="flex justify-between items-start">
                                    <div>
                                        <p className="font-bold text-gray-800">{formatSlotLabel(slot)}</p>
                                        <p className="text-xs text-gray-500 font-mono">{slot.id}</p>
                                    </div>
                                    {getStatusBadge(slot)}
                                </div>
                                <div className="mt-3 w-full bg-gray-200 rounded-full h-3">
                                    <div className={`h-3 rounded-full ${isFull ? 'bg-red-500' : 'bg-green-600'}`} style={{ width: `${pct}%` }}></div>
                                </div>
                                <p className="mt-1 text-sm font-bold">{terisi}/{total} terisi {isFull ? '• FULL' : `• tersisa ${sisa}`}</p>
                            </button>
                        );
                    })}
                </div>
            )}

            {selectedSlot && (
                <div className="mt-4 bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
                    <p className="font-bold text-gray-800">Booking: {formatSlotLabel(selectedSlot)}</p>
                    <div className="flex items-center gap-3 mt-3">
                        <span className="text-sm text-gray-600">Jumlah orang:</span>
                        <button onClick={() => setPax(1)} className={`px-4 py-2 rounded-lg font-bold ${pax === 1 ? 'bg-green-700 text-white' : 'bg-gray-100'}`}>1</button>
                        <button onClick={() => setPax(2)} className={`px-4 py-2 rounded-lg font-bold ${pax === 2 ? 'bg-green-700 text-white' : 'bg-gray-100'}`}>2</button>
                    </div>
                    <div className="mt-3 text-sm bg-gray-50 rounded-lg p-3">
                        <div className="flex justify-between"><span>Tiket ({pax} x Rp 10.000)</span><span className="font-bold">Rp {(pax * TICKET_PRICE).toLocaleString('id-ID')}</span></div>
                        <div className="flex justify-between"><span>Admin fee</span><span className="font-bold">Rp {ADMIN_FEE.toLocaleString('id-ID')}</span></div>
                        <div className="flex justify-between mt-1 pt-1 border-t"><span className="font-bold">Total bayar</span><span className="font-bold text-green-800">Rp {totalBayar.toLocaleString('id-ID')}</span></div>
                    </div>
                    <button
                        onClick={handleBooking}
                        disabled={isBooking}
                        className="mt-3 w-full py-3 bg-green-700 text-white font-bold rounded-xl hover:bg-green-800 disabled:opacity-50"
                    >
                        {isBooking ? 'Memproses...' : `Booking ${pax} orang • Bayar Rp ${totalBayar.toLocaleString('id-ID')}`}
                    </button>
                </div>
            )}

            <h2 className="mt-6 font-bold text-gray-800">Booking saya</h2>
            {myBookings.length === 0 ? (
                <p className="text-sm text-gray-500 mt-1">Belum ada booking promo.</p>
            ) : (
                <div className="mt-2 space-y-3">
                    {myBookings.map((b) => (
                        <div key={b.id} className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
                            <div className="flex justify-between items-start">
                                <div>
                                    <p className="font-bold text-sm">{b.promoSlotId} • {b.pax} orang</p>
                                    <p className="text-xs text-gray-500 font-mono">{b.kodeBooking || b.id.substring(0, 8)}</p>
                                </div>
                                <span className="text-xs font-bold px-2 py-1 rounded-full bg-gray-100">{b.status}</span>
                            </div>
                            {(b.status === 'paid' || b.status === 'redeemed') && (
                                <div className="flex justify-center mt-2 bg-gray-50 p-2 rounded-lg">
                                    <QRCodeSVG value={b.kodeBooking || b.id} size={120} level="M" includeMargin={false} />
                                </div>
                            )}
                            <p className="text-sm font-bold mt-2">Rp {(b.totalBayar || b.total || 0).toLocaleString('id-ID')}</p>
                            <div className="flex gap-2 mt-2">
                                {b.status === 'pending_payment' && b.paymentUrl && (
                                    <button onClick={() => { window.location.href = b.paymentUrl; }} className="px-3 py-1.5 bg-purple-600 text-white text-sm font-bold rounded-lg">Lanjut Bayar</button>
                                )}
                                {b.status === 'pending_payment' && (
                                    <button onClick={() => handleCheckStatus(b)} className="px-3 py-1.5 bg-gray-100 text-sm font-bold rounded-lg">Cek Status</button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default PromoWeekendScreen;
